import { useState, useRef, useEffect, ReactNode, useId } from 'react';

export interface TooltipProps {
  content: ReactNode;
  children: ReactNode;
  position?: 'top' | 'bottom' | 'left' | 'right';
  delay?: number;
  variant?: 'default' | 'error' | 'success' | 'warning' | 'info';
  size?: 'sm' | 'md' | 'lg';
}

export function Tooltip({
  content,
  children,
  position = 'top',
  delay = 0,
  variant = 'default',
  size = 'md'
}: TooltipProps) {
  const [isVisible, setIsVisible] = useState(false);
  const timeoutRef = useRef<NodeJS.Timeout>();
  const tooltipId = useId();

  const showTooltip = () => {
    if (delay > 0) {
      timeoutRef.current = setTimeout(() => {
        setIsVisible(true);
      }, delay);
    } else {
      setIsVisible(true);
    }
  };

  const hideTooltip = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    setIsVisible(false);
  };

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const variantClasses = {
    default: '',
    error: 'tooltip-error',
    success: 'tooltip-success',
    warning: 'tooltip-warning',
    info: 'tooltip-info'
  };

  const sizeClasses = {
    sm: 'tooltip-sm',
    md: 'tooltip-md',
    lg: 'tooltip-lg'
  };

  return (
    <div
      className="tooltip-wrapper"
      onMouseEnter={showTooltip}
      onMouseLeave={hideTooltip}
      onFocus={showTooltip}
      onBlur={hideTooltip}
    >
      <div
        className="tooltip-trigger"
        tabIndex={0}
        aria-describedby={content ? tooltipId : undefined}
      >
        {children}
      </div>

      {content && (
        <div
          id={tooltipId}
          role="tooltip"
          aria-hidden={!isVisible}
          className={`
            tooltip-content
            tooltip-${position}
            ${variantClasses[variant]}
            ${sizeClasses[size]}
            ${isVisible ? 'visible' : ''}
          `}
        >
          {content}
        </div>
      )}
    </div>
  );
}

// Preset tooltip for info icon
export function InfoTooltip({ content }: { content: ReactNode }) {
  return (
    <Tooltip content={content} position="top">
      <span className="info-tooltip">?</span>
    </Tooltip>
  );
}

// Helper component for help text with tooltip
export function HelpText({ children, tooltip }: { children: ReactNode; tooltip?: ReactNode }) {
  if (!tooltip) {
    return <div className="help-text">{children}</div>;
  }

  return (
    <div className="help-text">
      {children}
      <InfoTooltip content={tooltip} />
    </div>
  );
}

/**
 * Helper component for wrapping terms with dotted underline to indicate tooltip availability
 * Perfect for inline help on technical terms like FIFO, P&L, ATH, etc.
 */
export function TooltipTerm({ content, children, position = 'top' }: { content: ReactNode; children: ReactNode; position?: 'top' | 'bottom' | 'left' | 'right' }) {
  return (
    <Tooltip content={content} position={position} delay={200}>
      <span style={{
        borderBottom: '1px dotted var(--muted)',
        cursor: 'help',
        display: 'inline-block'
      }}>
        {children}
      </span>
    </Tooltip>
  );
}
